<script setup lang="ts">
import {useAppDataStore} from '@/stores/app-data'
import {useCharacterDataStore} from "@/stores/character-data";
import SkillFinder from "@/components/atoms/swade/SkillFinder.vue";
import LanguageSelector from "@/components/atoms/swade/LanguageSelector.vue";
import type {SwadeSkillRankInterface} from "@/types/items";
import type {SettingElementInterface} from "@/types/items";
import type {CharacterSheetInterface} from "@/types/characters";
import {resolveSkillTrapping} from "@/appdata/container-fields";
import {containerDisplayNameFor} from "@/appdata/chosen-labels";
import {useUxData} from "@/stores/ux-data.ts";
import {computed} from "vue";

const emit = defineEmits(['updated'])
const store = useAppDataStore();
const swade = useCharacterDataStore();
const ux = useUxData();

const props = withDefaults(
    defineProps<{
      showFinder?: boolean
    }>(),
    {
      showFinder: true,
    }
)
const character = defineModel<CharacterSheetInterface>('character')

const isSkillAdded = (element: SettingElementInterface): boolean => {
  return character.value?.skills?.some((item: SwadeSkillRankInterface) => item.skill.uuid === element.uuid) ?? false;
};

// Resolves a skill rank's *actual* linked attribute. Modifications can change a
// skill's linked attribute, and that change is recorded on the character's
// protoData skill (keyed by rankUuid, with the skill uuid as a fallback), never
// on the SkillRank itself. Fall back to the skill's own linked attribute when no
// proto entry exists.
const linkedAttributeFor = computed(() => {
  return (item: SwadeSkillRankInterface): string => {
    const protoSkills = character.value?.protoData?.skills;

    const protoSkill = Array.isArray(protoSkills)
      ? (protoSkills.find((proto: any) => proto.rankUuid === item.uuid)
        ?? protoSkills.find((proto: any) => proto.uuid === item.skill?.uuid))
      : undefined;

    return protoSkill?.linkedAttribute ?? item.skill?.linkedAttribute ?? '';
  };
});

const onSkillSelected = (event: any) => {
  console.info("SWADE/Skills | onSkillSelected");
  if (!event.item) return;
  if ((event.value ?? "") === "") return;

  console.debug("SWADE/Skills | onSkillSelected", character.value, event.item);
  swade.addSkillToCharacter(character.value, event.item);
  swade.selections.skill = null;

  emit('updated', event)
}

const onModelChanged = (event: any) => {
  emit('updated', event)
}

const onRemoveSkillClick = (event: any, item: SwadeSkillRankInterface) => {
  swade.removeSkill(character.value, item.skill, item.uuid);
  if (item.skill.uuid === swade.buildOptions.languages?.skill) {
    // The remove button that reaches this handler only exists inside the template's
    // v-if="character" root, so character.value is set whenever this runs; asserted rather than
    // guarded so an unset model still throws instead of silently skipping the language cleanup.
    const sheet = character.value as CharacterSheetInterface;
    sheet.protoData.languages = sheet.protoData.languages.filter((language: { label: string, slotsUsed?: number }) => {
      return language.label !== resolveSkillTrapping(item);
    })
  }

  emit('updated', event)

  swade.recalculateDerivedStats()
}

const onNativeLanguageSelect = (event: any, item: SwadeSkillRankInterface) => {
  const protoSkill = swade.protoLanguageSkill(character.value, item);
  if (protoSkill) {
    // Same reachability as onRemoveSkillClick above: this only fires from a button inside the
    // v-if="character" template root.
    (character.value as CharacterSheetInterface).skills?.forEach((skillRank: SwadeSkillRankInterface) => {
      if (skillRank.skill.uuid !== swade.buildOptions?.languages?.skill) return;

      const protoItem = swade.protoLanguageSkill(character.value, skillRank);
      if (skillRank.rank === protoItem.base) {
        skillRank.rank = 'd6';
      }

      protoItem.base = 'd6';
    })

    protoSkill.base = 'd8';
    if (swade.dieRankValue(item.rank) < 8) {
      item.rank = 'd8';
    }
  }

  emit('updated', event);
}
</script>

<template>
  <section v-if="character" class="skill-list">
    <div class="list-group list-group-flush">
      <LanguageSelector v-model:character="character" @updated="onModelChanged" />
      <div class="list-group-item header skill-list-header">{{ ux.labels.swade.data.characters.skills }}</div>
      <div v-if="store.props.edit && showFinder" class="list-group-item finder">
        <label class="sr-only">{{ ux.labels.characterCreator.actions.addSkill }}</label>
        <SkillFinder
            :placeholder="ux.labels.characterCreator.actions.addSkill"
            :browsable="true"
            placement="bottom"
            :is-added="isSkillAdded"
            :filters="{ world: character?.world ?? null }"
            @skill-selected="onSkillSelected"
        />
      </div>
      <div v-for="item in character.skills" class="list-group-item skill pe-1" v-bind:key="item.uuid">
        <div class="d-flex justify-space-between">
          <span class="skill-name">
            <span class="core-skill-indicator d-print-none" v-if="swade.buildOptions?.skills.core.includes(item.skill.uuid)">
              <span class="fas fa-fw fa-star"></span>
            </span>
            <span class="my-auto">{{ containerDisplayNameFor(character, item) }}</span>
            <button v-if="store.props.edit && !swade.hasActiveAdvances(character) && item.skill.uuid === swade.buildOptions?.languages?.skill"
                    type="button"
                    class="native-language-indicator badge btn btn-sm"
                    :class="swade.protoLanguageSkill(character, item)?.base === 'd8' ? 'btn-info' : 'btn-outline-info'"
                    @click="onNativeLanguageSelect($event, item)"
            >
              {{ ux.labels.swade.characterCreator.languages.native }}
            </button>
            <span v-else-if="item.skill.uuid === swade.buildOptions?.languages?.skill && swade.protoLanguageSkill(character, item)?.base === 'd8'"
                  class="badge text-white bg-info">{{ ux.labels.swade.characterCreator.languages.native }}</span>
            <span class="linked-attribute">
              {{ ux?.labels?.swade?.attributes?.[linkedAttributeFor(item)] }}
              <span class="fa-light fa-link"></span>
            </span>
          </span>
          <span class="skill-rank" v-if="!store.props.edit">
            {{ item.rank }}<span v-if="item.modifier"><span v-if="(item.modifier ?? 0) > 0">+</span>{{ item.modifier }}</span>
          </span>
          <div class="input-group skill-rank order-lg-0 order-last" v-if="store.props.edit">
            <select
                v-model="item.rank"
                class="form-select"
                @change="onModelChanged"
            >
              <option v-for="step in swade.attributeSteps"
                      :value="step.value"
                      :selected="step.value === item.rank"
              >
                {{ step.title }}
              </option>
            </select>
            <div v-if="(item?.modifier ?? 0) >= 0" class="input-group-text">+</div>
            <input v-model="item.modifier"
                   @change="onModelChanged"
                   type="number"
                   min="-10"
                   class="form-control skill-modifier"
            />
          </div>
          <span v-if="store.props.edit" class="action my-auto">
            <button type="button" class="btn btn-link btn-sm px-0"
                    @click="onRemoveSkillClick($event, item)"
            >
              <span class="fa-light fa-times-circle"></span>
            </button>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
